import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Users, Save, Loader2, Edit } from "lucide-react";
import { toast } from "sonner";

const fmt = (n) => `${Number(n||0).toLocaleString("fr-FR")} GNF`;

export default function EmployesPage() {
  const [configs, setConfigs] = useState([]);
  const [users, setUsers] = useState([]);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    user_id: "", employment_type: "admin_monthly",
    base_salary_gnf: 0, hourly_rate_gnf: 0, position: "",
    cnss_rate_pct: 5, tax_rate_pct: 5,
    apply_cnss: true, apply_tax: true, fixed_bonus_gnf: 0, hire_date: "",
  });

  const load = async () => {
    try {
      const [{ data: cs }, { data: us }] = await Promise.all([
        api.get("/payroll/configs"),
        api.get("/users"),
      ]);
      setConfigs(cs);
      setUsers(us.filter(u => u.role && ["teacher","admin","finance","director","founder","scolarite","nurse","canteen"].includes(u.role)));
    } catch { toast.error("Erreur"); }
  };
  useEffect(() => { load(); }, []);

  const editUser = (u) => {
    const cfg = configs.find(c => c.user_id === u.id) || {
      user_id: u.id, employment_type: u.role === "teacher" ? "teacher_hourly" : "admin_monthly",
      base_salary_gnf: 0, hourly_rate_gnf: 0, position: u.role,
      cnss_rate_pct: 5, tax_rate_pct: 5, apply_cnss: true, apply_tax: true, fixed_bonus_gnf: 0,
    };
    setForm({ ...form, ...cfg });
    setOpen(true);
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.post("/payroll/config", {
        ...form,
        base_salary_gnf: Number(form.base_salary_gnf) || 0,
        hourly_rate_gnf: Number(form.hourly_rate_gnf) || 0,
        cnss_rate_pct: Number(form.cnss_rate_pct) || 0,
        tax_rate_pct: Number(form.tax_rate_pct) || 0,
        fixed_bonus_gnf: Number(form.fixed_bonus_gnf) || 0,
      });
      toast.success("Configuration enregistrée");
      setOpen(false);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
    finally { setSaving(false); }
  };

  const configuredUserIds = new Set(configs.map(c => c.user_id));

  return (
    <div className="space-y-6" data-testid="employes-page">
      <div>
        <div className="chip mb-2"><Users className="h-3 w-3" /><span>CONFIGURATION PAIE</span></div>
        <h1 className="font-heading font-extrabold text-3xl text-[#1E3A8A]">Employés & configuration salariale</h1>
        <p className="text-slate-500 mt-1">Paramétrez le salaire de base ou taux horaire, CNSS et impôts de chaque employé.</p>
      </div>

      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50">
              <tr className="text-left text-xs uppercase tracking-wider text-slate-500">
                <th className="px-4 py-3">Employé</th>
                <th className="px-4 py-3">Poste / Rôle</th>
                <th className="px-4 py-3">Type</th>
                <th className="px-4 py-3 text-right">Base / Taux</th>
                <th className="px-4 py-3">CNSS/Impôt</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {users.map(u => {
                const cfg = configs.find(c => c.user_id === u.id);
                return (
                  <tr key={u.id} className="border-t border-slate-100 hover:bg-slate-50">
                    <td className="px-4 py-3"><div className="font-semibold text-[#1E3A8A]">{u.full_name}</div><div className="text-xs text-slate-500">{u.email}</div></td>
                    <td className="px-4 py-3">{cfg?.position || <span className="italic text-slate-400">{u.role}</span>}</td>
                    <td className="px-4 py-3">
                      {cfg ? (
                        <span className={`text-xs px-2 py-0.5 rounded font-bold ${cfg.employment_type === "teacher_hourly" ? "bg-purple-100 text-purple-800" : "bg-blue-100 text-blue-800"}`}>
                          {cfg.employment_type === "teacher_hourly" ? "Horaire" : "Mensuel"}
                        </span>
                      ) : <span className="text-xs text-slate-400">Non configuré</span>}
                    </td>
                    <td className="px-4 py-3 text-right tabular-nums">
                      {cfg ? (cfg.employment_type === "teacher_hourly" ? `${fmt(cfg.hourly_rate_gnf)}/h` : fmt(cfg.base_salary_gnf)) : "—"}
                    </td>
                    <td className="px-4 py-3 text-xs">
                      {cfg ? (
                        <>{cfg.apply_cnss && <span className="text-slate-600">CNSS {cfg.cnss_rate_pct}% </span>}{cfg.apply_tax && <span className="text-slate-600">Impôt {cfg.tax_rate_pct}%</span>}</>
                      ) : "—"}
                    </td>
                    <td className="px-4 py-3 text-right"><Button size="sm" variant="outline" onClick={() => editUser(u)} data-testid={`edit-${u.id}`}><Edit className="h-3 w-3 mr-1" />{configuredUserIds.has(u.id) ? "Modifier" : "Configurer"}</Button></td>
                  </tr>
                );
              })}
              {users.length === 0 && <tr><td colSpan={6} className="px-4 py-10 text-center text-slate-400">Aucun employé.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle>Configuration salariale</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Type de rémunération</Label>
                <Select value={form.employment_type} onValueChange={(v) => setForm({...form, employment_type: v})}>
                  <SelectTrigger data-testid="employment-type"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="teacher_hourly">Enseignant — à l&apos;heure</SelectItem>
                    <SelectItem value="admin_monthly">Personnel administratif — mensuel</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div><Label>Poste / fonction</Label><Input value={form.position} onChange={e => setForm({...form, position: e.target.value})} placeholder="Directeur, Comptable, Enseignant Maths..." data-testid="position" /></div>
              {form.employment_type === "teacher_hourly" ? (
                <div className="col-span-2"><Label>Taux horaire (GNF/heure)</Label><Input type="number" value={form.hourly_rate_gnf} onChange={e => setForm({...form, hourly_rate_gnf: e.target.value})} data-testid="hourly-rate" /></div>
              ) : (
                <div className="col-span-2"><Label>Salaire de base mensuel (GNF)</Label><Input type="number" value={form.base_salary_gnf} onChange={e => setForm({...form, base_salary_gnf: e.target.value})} data-testid="base-salary" /></div>
              )}
              <div className="col-span-2"><Label>Prime fixe mensuelle (transport, logement, etc.) - GNF</Label><Input type="number" value={form.fixed_bonus_gnf} onChange={e => setForm({...form, fixed_bonus_gnf: e.target.value})} data-testid="fixed-bonus" /></div>
              <div className="col-span-2 grid grid-cols-2 gap-3 border-t border-slate-200 pt-3">
                <label className="flex items-center gap-2 text-sm">
                  <input type="checkbox" checked={form.apply_cnss} onChange={e => setForm({...form, apply_cnss: e.target.checked})} data-testid="apply-cnss" />
                  Appliquer la CNSS
                </label>
                <div><Label>Taux CNSS (%)</Label><Input type="number" step="0.5" value={form.cnss_rate_pct} onChange={e => setForm({...form, cnss_rate_pct: e.target.value})} disabled={!form.apply_cnss} /></div>
                <label className="flex items-center gap-2 text-sm">
                  <input type="checkbox" checked={form.apply_tax} onChange={e => setForm({...form, apply_tax: e.target.checked})} data-testid="apply-tax" />
                  Appliquer l&apos;impôt sur salaire (IRPP)
                </label>
                <div><Label>Taux IRPP (%)</Label><Input type="number" step="0.5" value={form.tax_rate_pct} onChange={e => setForm({...form, tax_rate_pct: e.target.value})} disabled={!form.apply_tax} /></div>
              </div>
              <div className="col-span-2"><Label>Date d&apos;embauche</Label><Input type="date" value={form.hire_date || ""} onChange={e => setForm({...form, hire_date: e.target.value})} /></div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Annuler</Button>
            <Button onClick={save} disabled={saving} className="bg-[#166534] text-white" data-testid="save-config">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Save className="h-4 w-4 mr-2" />Enregistrer</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
